import type { EarthingRunCreateRequest } from "../services/earthing";
import { downloadRunJson } from "../services/runExport";
import { EarthingForm } from "../components/EarthingForm";
import { EarthingLinkPanel } from "../components/EarthingLinkPanel";
import { EarthingResultPanel } from "../components/EarthingResultPanel";
import { EarthingResultSummary } from "../components/EarthingResultSummary";
import { EarthingWarningPanel } from "../components/EarthingWarningPanel";
import { RunTraceabilityPanel } from "../components/RunTraceabilityPanel";
import { SldLinkPanel } from "../components/SldLinkPanel";
import { StudyProjectLine } from "../components/StudyProjectLine";
import { useEarthing } from "../hooks/useEarthing";
import { useSldElements } from "../hooks/useSldElements";

function describeError(error: unknown): string {
  if (error instanceof Error && error.message.trim().length > 0) {
    return error.message;
  }
  return "The earthing study could not be completed.";
}

// Study-page layout (Master Prompt v2.1 section 19): collapsible inputs beside a result-first
// column - summary, the linked runs, the SLD link, warnings, traceability, then the detail
// tables. EOS-08a's one page: the earthing arrangement of one bus.
export function EarthingPage() {
  const { calculate, reset, result, run, error, isPending, isError } = useEarthing();
  // The active revision's single-line diagram, for the bus and feeder pickers (A27 (b)).
  const { elements: sldElements } = useSldElements();

  // Errors are surfaced through the hook state below, not thrown into the form.
  async function handleSubmit(payload: EarthingRunCreateRequest): Promise<void> {
    try {
      await calculate(payload);
    } catch {
      // Intentionally empty: isError / error already describe the failure.
    }
  }

  return (
    <main data-earthing-page>
      <header>
        <p>EOS-08 · Earthing</p>
        <h1>Earthing</h1>
        <p>
          Check the earthing arrangement of one bus: each protective conductor's area against the
          adiabatic requirement of IS 3043:2018 (S = √(I²t) / k, with the current and the time read
          from the linked earth-fault and earth-fault loop runs or declared, and k from the held
          IS 3043 and CPWD tables or declared), and the resistance of the earth electrodes from the
          printed formulas at the measured soil resistivity against the declared target, using the
          validated KES Electrical OS calculation service. Every figure is a stored run's or a
          declared value with its source; nothing is assumed.
        </p>
      </header>

      <div data-study-layout>
        <section aria-labelledby="earthing-inputs-heading">
          <details open data-study-inputs>
            <summary>
              <h2 id="earthing-inputs-heading">Inputs</h2>
            </summary>
            <StudyProjectLine />
            <EarthingForm disabled={isPending} onSubmit={handleSubmit} sldElements={sldElements} />
          </details>
        </section>

        <section aria-labelledby="earthing-results-heading" data-study-results>
          <h2 id="earthing-results-heading">Results</h2>

          {isPending ? (
            <p role="status" data-calculation-state="pending">
              Checking the earthing…
            </p>
          ) : null}

          {isError ? (
            <p role="alert" data-calculation-state="error">
              {describeError(error)}
            </p>
          ) : null}

          {result ? (
            <div data-calculation-state="success">
              <EarthingResultSummary result={result} />
              <EarthingLinkPanel result={result} />
              {run ? <SldLinkPanel link={run.sld_link} /> : null}
              <EarthingWarningPanel result={result} />
              {run ? (
                <RunTraceabilityPanel
                  run={run}
                  onExport={() =>
                    downloadRunJson(
                      { run, result },
                      `${run.calculation_key}-rev${run.revision_number}.json`,
                    )
                  }
                />
              ) : null}
              <EarthingResultPanel result={result} />
              <p>
                <button type="button" onClick={reset}>
                  Clear results
                </button>
              </p>
            </div>
          ) : null}

          {!isPending && !isError && !result ? (
            <p data-calculation-state="idle">Submit the inputs to check the earthing.</p>
          ) : null}
        </section>
      </div>

      <section aria-labelledby="earthing-review-heading">
        <h2 id="earthing-review-heading">Engineering review required</h2>
        <p>
          Calculation results are engineering evidence, not an automatic compliance declaration.
          Confirm the soil resistivity test, the device data, every declared figure with its source
          and the linked fault and earth-fault loop studies before approval or issue. The step and
          touch voltages, the lightning earthing, the loop and bonding continuity, the corrosion
          allowance, the electrode current loading, the minimum dimensions, the selection table,
          the bonding conductors and the joints' temperatures are not evaluated here.
        </p>
      </section>
    </main>
  );
}

export default EarthingPage;
